/**
 * Color scale utilities for OD analysis visualization.
 * Trip intensity uses the shared workbook scale (scala_rgb_1_10.xlsx):
 * few trips are green, the busiest zones are red.
 */
import { badnessColor } from '../../../constants/scoreScale';

/**
 * Get color for a given percentage value
 * @param {number} percent - Value between 0 and 100
 * @returns {string} Hex color string
 */
export const getColorForPercentage = (percent) => {
  const clampedPercent = Math.max(0, Math.min(100, Number(percent) || 0));
  return badnessColor(clampedPercent / 100);
};

/**
 * Get hex color for a given percentage value
 * @param {number} percent - Value between 0 and 100
 * @returns {string} Hex color string
 */
export const getHexColorForPercentage = getColorForPercentage;

/**
 * Calculate percentage based on trips value and maximum
 * @param {number} trips - Number of trips
 * @param {number} maxTrips - Maximum number of trips
 * @returns {number} Percentage value (0-100)
 */
export const calculatePercentage = (trips, maxTrips) => {
  if (!maxTrips || maxTrips === 0) return 0;
  return (trips / maxTrips) * 100;
};

/**
 * Get the legend color stops for display
 * @returns {Array} Array of { label, color } objects
 */
export const getLegendStops = () => {
  return [
    { label: '0% - 19%', color: getColorForPercentage(0), percent: 0 },
    { label: '20% - 39%', color: getColorForPercentage(20), percent: 20 },
    { label: '40% - 59%', color: getColorForPercentage(40), percent: 40 },
    { label: '60% - 79%', color: getColorForPercentage(60), percent: 60 },
    { label: '80% - 89%', color: getColorForPercentage(80), percent: 80 },
    { label: '90% - 100%', color: getColorForPercentage(100), percent: 100 },
  ];
};

/**
 * Default zone style (no selection)
 */
export const defaultZoneStyle = {
  fillColor: '#d3d3d3',
  color: '#808080',
  weight: 2,
  opacity: 1,
  fillOpacity: 0.1,
};

/**
 * Get zone style based on trips data
 * @param {number} trips - Number of trips for this zone
 * @param {number} maxTrips - Maximum trips across all zones
 * @param {boolean} isOrigin - Whether this zone is the selected origin
 * @param {boolean} isDestination - Whether this zone is the selected destination
 * @returns {object} Leaflet path style object with percentage
 */
export const getZoneStyle = (trips, maxTrips, isOrigin, isDestination) => {
  const percent = calculatePercentage(trips, maxTrips);
  const fillColor = getColorForPercentage(percent);

  // Selected origin - gray fill with green border (origin is starting point, no coloring)
  if (isOrigin) {
    return {
      ...defaultZoneStyle,
      color: '#2E7D32',
      weight: 3,
      percent: 0,
    };
  }

  // Selected destination - colored based on trips with blue border
  if (isDestination) {
    return {
      fillColor: trips > 0 ? fillColor : defaultZoneStyle.fillColor,
      color: '#1565C0',
      weight: 3,
      opacity: 1,
      fillOpacity: 0.7,
      percent,
    };
  }

  // If no trips data, use default gray
  if (trips === undefined || trips === null || trips === 0) {
    return { ...defaultZoneStyle, percent: 0 };
  }

  return {
    fillColor,
    color: '#404040',
    weight: 2,
    opacity: 1,
    fillOpacity: 0.7,
    percent,
  };
};
